import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import hautVoixPosterAsset from "@/assets/affiche-soiree-a-haute-voix-2026-save-the-date.png.asset.json";

const hautVoixPosterUrl = `https://enfants-de-la-republique.lovable.app${hautVoixPosterAsset.url}`;

const highlights = [
  {
    number: "01.",
    title: "Le constat",
    body: (
      <>
        Présentation en avant-première de l’ICF 2026, table ronde avec des
        personnalités du monde intellectuel, médiatique et des témoignages de
        terrain pour dresser l’état des lieux de la fracture citoyenne.
      </>
    ),
    className: "bg-eventYellow",
  },
  {
    number: "02.",
    title: "La proposition",
    body: (
      <>
        Comment faire vivre les valeurs et les principes républicains ? Une
        Charte plus vivante et engageante ? Dialogue croisé entre la réalité du
        terrain, la force des idées et le cadre de la règle pour des solutions
        républicaines.
      </>
    ),
    className: "bg-fuchsia-300",
  },
  {
    number: "03.",
    title: "L’action",
    body: (
      <>
        Deux appels aux dons en direct pour deux projets concrets qui nous
        engagent : <strong>l’Observatoire Enfants de la République</strong> et
        le concours <strong>Exprime la Charte</strong>.
      </>
    ),
    className: "bg-sky-400",
  },
];

const ProjectList = ({ children }: { children: React.ReactNode }) => (
  <ul className="space-y-3 text-sm leading-relaxed list-disc pl-5">{children}</ul>
);

export const HautVoixFeature = () => {
  return (
    <article className="space-y-8 text-[#1E3A8A]">
      <div className="grid gap-4 lg:grid-cols-2 lg:items-stretch">
        <div className="relative aspect-[1080/1350] w-full overflow-hidden border border-black/40 shadow-lg">
          <img
            src={hautVoixPosterUrl}
            alt="Affiche de la soirée À Haute Voix du 5 novembre 2026 au Théâtre de la Tour Eiffel"
            className="absolute inset-0 h-full w-full object-contain"
            loading="eager"
          />
        </div>

        <div className="flex h-full flex-col gap-4">
          <section className="flex flex-1 flex-col justify-center bg-white p-5 shadow-none">
            <h2 className="mb-2 text-lg font-bold text-[#1E3A8A]">Pourquoi maintenant ?</h2>
            <div className="space-y-2 text-sm leading-snug text-[#1E3A8A]">
              <p>
                À six mois de l’élection présidentielle de 2027, le débat public se
                structure. La fenêtre pour installer une grille de lecture
                républicaine factuelle dans ce débat est désormais ouverte.
              </p>
              <p>
                <strong>La Soirée À Haute Voix du 5 novembre 2026</strong> est un acte
                civique. Elle rassemblera des personnalités issues du monde
                intellectuel, économique, culturel et médiatique, engagées dans la
                réflexion et la transmission. Elle vise à créer un espace où les
                faits peuvent être exposés, où les points de vue peuvent se croiser,
                et où une parole exigeante peut émerger, sans simplification ni
                opposition stérile.
              </p>
              <p>Le choix du nom <strong>À Haute Voix</strong> traduit cette intention :</p>
              <div className="border-l-4 border-[#1E3A8A] pl-3 font-semibold">
                <p>Faire entendre ce qui est souvent implicite.</p>
                <p>Redonner de la clarté à ce qui est devenu flou.</p>
                <p>Permettre une expression qui ne divise pas mais qui éclaire.</p>
              </div>
            </div>
          </section>

          <section className="flex flex-1 flex-col justify-center bg-white p-5 shadow-none">
            <h2 className="mb-2 text-lg font-bold text-[#1E3A8A]">
              Des intervenants de premier plan
            </h2>
            <div className="space-y-2 text-sm leading-snug text-[#1E3A8A]">
              <p>
                Une soirée animée par <strong>Fabrice Lundy</strong> avec des personnalités de premier plan de la société civile et des invités exceptionnels qui prendront la parole.
              </p>
              <p>La liste définitive sera publiée le 5 octobre.</p>
            </div>
          </section>
        </div>
      </div>

      <Link
        to="/inscription-AHV2026"
        className="group relative block w-full overflow-hidden rounded-xl bg-gradient-to-r from-[#15153a] via-[#1E3A8A] to-[#4c51bf] py-3 text-center shadow-lg transition-all hover:shadow-xl hover:scale-[1.01]"
      >
        {/* Accent diagonal rouge/rose inspiré de l'affiche */}
        <div className="absolute -right-4 -top-4 h-14 w-14 rotate-12 bg-[#e11d48] opacity-90 transition-transform group-hover:rotate-6" />

        <div className="relative flex flex-col items-center justify-center gap-0.5">
          <span className="inline-block rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#15153a]">
            Soirée Exceptionnelle
          </span>
          <span className="text-lg font-black uppercase tracking-tight text-white sm:text-xl">
            Se préinscrire
          </span>
          <span className="text-xs font-semibold text-white/90">
            À Haute Voix · 05 · 11 · 2026
          </span>
        </div>
      </Link>

      <section>
        <h2 className="mb-4 text-xl font-bold text-[#1E3A8A]">Trois temps forts</h2>
        <div className="grid gap-3 md:grid-cols-3">
          {highlights.map((highlight) => (
            <div key={highlight.number} className={`${highlight.className} min-h-full p-5 text-[#001233]`}>
              <p className="text-sm font-bold text-[#001233]">{highlight.number}</p>
              <h3 className="mb-3 text-xl font-bold text-[#001233]">{highlight.title}</h3>
              <p className="text-sm leading-relaxed text-[#001233]">{highlight.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white p-5 shadow-sm">
        <div className="space-y-3 text-sm leading-relaxed text-[#1E3A8A] md:text-[15px]">
          <p>
            En plus du présentiel au Théâtre de la Tour Eiffel (400 places), la
            soirée sera diffusée en direct sur le web et relayée sur de nombreux médias.
          </p>
          <p>
            La <strong>Soirée À Haute Voix</strong> doit constituer un point
            d’accélération de la conscience citoyenne et se faire le porte-voix de
            ceux qui sont profondément attachés aux valeurs républicaines et trop
            souvent silencieux.
          </p>
          <p>
            <strong>Pour y contribuer pleinement, Enfants de la République doit</strong>{" "}
            installer durablement <strong>l’Indice de Citoyenneté Française</strong>{" "}
            comme un rendez-vous de référence et renforcer la visibilité de la
            Charte comme un cadre incontournable du débat public.
          </p>
        </div>
      </section>

      <section>
        <h2 className="mb-4 text-xl font-bold text-[#1E3A8A]">
          L’action en projets — appels aux dons
        </h2>
        <div className="space-y-5">
          <div className="bg-eventYellow p-5 text-[#001233]">
            <h3 className="mb-4 text-xl font-bold text-[#001233]">Projet 1 : Agir</h3>
            <div className="grid gap-5 md:grid-cols-2">
              <div className="space-y-3 text-sm leading-relaxed text-[#001233]">
                <p className="font-bold">La création de l’Observatoire Enfants de la République</p>
                <p className="font-semibold">
                  L’Observatoire ne sera pas qu’un simple centre d’études. À l’heure
                  des fake news et de la polarisation, il offrira une grille de
                  lecture rigoureuse et objective.
                </p>
                <ProjectList>
                  <li><strong>Le comité d’experts :</strong> constitution d’un collège pluridisciplinaire de cinq experts chargé de garantir la rigueur des travaux et la production de « Notes de l’Observatoire ».</li>
                  <li><strong>L’Indice de Citoyenneté Française 2027 (ICF)</strong>, accompagné d’une enquête d’opinion nationale et d’une analyse complète pour créer un baromètre annuel.</li>
                </ProjectList>
              </div>
              <ProjectList>
                <li><strong>Audit des programmes des candidats à la présidentielle :</strong> analyse des programmes de 2027 au prisme des principes de la Charte afin de déterminer le niveau de compatibilité républicaine de chaque mesure proposée.</li>
                <li><strong>Un prix annuel de l’Observatoire</strong> pour une association, afin de reconnaître une action exemplaire pour chacune de nos valeurs républicaines : Liberté, Égalité, Fraternité, Laïcité.</li>
              </ProjectList>
            </div>
          </div>

          <div className="bg-sky-400 p-5 text-[#001233]">
            <h3 className="mb-4 text-xl font-bold text-[#001233]">Projet 2 : Transmettre</h3>
            <div className="grid gap-5 md:grid-cols-2">
              <div className="space-y-3 text-sm leading-relaxed text-[#001233]">
                <p className="font-semibold">
                  Passer d’une citoyenneté passive à une citoyenneté active parce
                  que les valeurs et principes républicains ne doivent pas rester
                  des textes figés. Ce projet vise à les rendre vivants, désirables
                  et accessibles à tous.
                </p>
                <p><strong>1. Le Concours « Exprime la Charte » :</strong> nous changeons d’échelle pour déployer ce concours au niveau national et inviter la jeunesse à s’approprier les articles de la Charte de manière vivante et concrète.</p>
                <ProjectList>
                  <li><strong>Le concept :</strong> traduire les valeurs républicaines en productions artistiques de tous formats : écriture, arts visuels, musique, vidéos ou podcasts.</li>
                  <li><strong>L’impact :</strong> demander aux jeunes d’incarner la Charte et favoriser le dialogue entre eux.</li>
                  <li><strong>L’objectif :</strong> accompagner 20 à 30 groupes de jeunes sur tout le territoire et valoriser leurs œuvres lors d’une cérémonie à fort écho médiatique.</li>
                </ProjectList>
              </div>
              <div className="space-y-3 text-sm leading-relaxed text-[#001233]">
                <p><strong>2. Un programme de conférences :</strong> un cycle d’événements itinérants pour réconcilier la réflexion de fond avec l’urgence du débat citoyen.</p>
                <ProjectList>
                  <li><strong>Le concept :</strong> utiliser un essai, une œuvre littéraire ou un film documentaire comme porte d’entrée pour organiser un débat avec des personnalités et le public.</li>
                  <li><strong>La méthode :</strong> déployer ces rencontres partout en France, des institutions prestigieuses aux espaces de proximité.</li>
                  <li><strong>L’impact :</strong> restaurer la qualité du débat d’idées et offrir un espace de respiration intellectuelle pour mieux comprendre la société.</li>
                  <li><strong>L’objectif :</strong> organiser une conférence par mois à partir de janvier 2027.</li>
                </ProjectList>
              </div>
            </div>
          </div>
        </div>
      </section>

      <blockquote className="bg-yellow-300 px-5 py-4 text-center font-semibold italic text-[#001233]">
        Les droits progressent pendant que les devoirs s’effondrent.<br />
        l'ICF25 montre clairement que le déséquilibre n’est plus théorique
      </blockquote>
    </article>
  );
};